// 公開版APIクライアント。Pages Functions の /api/public 配下を叩く。
// 既存 src/api.ts の書き方（req ラッパ・pollCheck）を踏襲。
import type {
  PublicCheckRequest,
  PublicCheckResponse,
  PublicCheckStatusResponse,
} from '@shared/types';

const BASE = '/api';

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    // レート制限(429)含め、レスポンスbodyの error メッセージを優先して伝播する
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch {
      // ignore parse error
    }
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** 公開チェック申込を送信。429時は body.error メッセージを Error に載せて throw。 */
export function submitPublicCheck(
  input: PublicCheckRequest,
): Promise<PublicCheckResponse> {
  return req<PublicCheckResponse>('/public/checks', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** 公開チェックの状態を取得。 */
export function getPublicCheckStatus(
  checkId: string,
): Promise<PublicCheckStatusResponse> {
  return req<PublicCheckStatusResponse>(
    `/public/checks/${encodeURIComponent(checkId)}`,
  );
}

/**
 * 公開チェックジョブをポーリングして完了まで待つ。
 * running の間 intervalMs おきに再取得。done/error で解決。
 * PSI/Geminiで時間がかかるため timeout は既存 pollCheck より長めの 150 秒。
 */
export async function pollPublicCheck(
  checkId: string,
  opts: {
    intervalMs?: number;
    timeoutMs?: number;
    onTick?: (s: PublicCheckStatusResponse) => void;
  } = {},
): Promise<PublicCheckStatusResponse> {
  const intervalMs = opts.intervalMs ?? 2500;
  const timeoutMs = opts.timeoutMs ?? 150_000;
  const start = Date.now();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const s = await getPublicCheckStatus(checkId);
    opts.onTick?.(s);
    if (s.status !== 'running') return s;
    if (Date.now() - start > timeoutMs) {
      throw new Error(
        '診断がタイムアウトしました。お手数ですが時間をおいて再度お試しください。',
      );
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}
